/**
 * GST compliance helpers for Development Express (MachineOS).
 * Sourced from ICAI "GST Act(s) and Rule(s) — Bare Law" (12th ed., Apr 2026),
 * amendments up to 31 Mar 2026.
 *
 * Platform role: electronic commerce operator (CGST Act s.2(45)) collecting
 * consideration → TCS under s.52; tax invoices under s.31 + Rule 46.
 */

const LEGAL = {
  source: 'GST Act(s) and Rule(s) — Bare Law (ICAI, 12th ed., April 2026)',
  amendedUpto: '2026-03-31',
  citations: {
    ecoDefinition: 'CGST Act s.2(44)–(45)',
    taxInvoice: 'CGST Act s.31',
    taxIndicated: 'CGST Act s.33',
    tcs: 'CGST Act s.52',
    compulsoryRegEco: 'CGST Act s.24(x)',
    invoiceParticulars: 'CGST Rules r.46',
    billOfSupply: 'CGST Act s.31(3)(c) read with CGST Rules r.49',
    timeOfSupplyServices: 'CGST Act s.13 / s.31(5)',
    placeOfSupplyServices: 'IGST Act Ch.V (ss.12–13)',
  },
};

/** Standard rate for construction-machinery hire (operator + fuel) — SAC 997313. */
const OUTPUT_GST_RATE = 0.18;
const CGST_RATE = 0.09;
const SGST_RATE = 0.09;
const IGST_RATE = 0.18;

/**
 * s.52(1): TCS at notified rate not exceeding 1% of net value of taxable supplies.
 * Notified rate for ECO: 1% (0.5% CGST + 0.5% SGST / 1% IGST).
 */
const TCS_RATE_MAX = 0.01;
const TCS_RATE_DEFAULT = 0.01;

/** SAC for leasing or rental services concerning construction machinery and equipment with or without operator — Notification 11/2017-CT(R), Heading 9973. */
const DEFAULT_SAC = '997313';

/** Rule 46(b): consecutive serial ≤ 16 characters. */
const INVOICE_SERIAL_MAX_LEN = 16;

/** Rule 46(e): unregistered recipient address mandatory if taxable value ≥ ₹50,000. */
const UNREGISTERED_ADDRESS_THRESHOLD = 50000;

/** s.52(3): TCS remittance within 10 days after month end. */
const TCS_REMIT_DAYS_AFTER_MONTH = 10;

const STATE_CODES = {
  '01': 'Jammu and Kashmir',
  '02': 'Himachal Pradesh',
  '03': 'Punjab',
  '04': 'Chandigarh',
  '05': 'Uttarakhand',
  '06': 'Haryana',
  '07': 'Delhi',
  '08': 'Rajasthan',
  '09': 'Uttar Pradesh',
  '10': 'Bihar',
  '11': 'Sikkim',
  '12': 'Arunachal Pradesh',
  '13': 'Nagaland',
  '14': 'Manipur',
  '15': 'Mizoram',
  '16': 'Tripura',
  '17': 'Meghalaya',
  '18': 'Assam',
  '19': 'West Bengal',
  '20': 'Jharkhand',
  '21': 'Odisha',
  '22': 'Chhattisgarh',
  '23': 'Madhya Pradesh',
  '24': 'Gujarat',
  '26': 'Dadra and Nagar Haveli and Daman and Diu',
  '27': 'Maharashtra',
  '29': 'Karnataka',
  '30': 'Goa',
  '31': 'Lakshadweep',
  '32': 'Kerala',
  '33': 'Tamil Nadu',
  '34': 'Puducherry',
  '35': 'Andaman and Nicobar Islands',
  '36': 'Telangana',
  '37': 'Andhra Pradesh',
  '38': 'Ladakh',
  '97': 'Other Territory',
};

/** Development Express — supplier defaults (Maharashtra). Override via env. */
const PLATFORM_SUPPLIER = {
  name: (typeof process !== 'undefined' && process.env && process.env.REACT_APP_DE_LEGAL_NAME) || 'DEVELOPMENT EXPRESS',
  address: (typeof process !== 'undefined' && process.env && process.env.REACT_APP_DE_LEGAL_ADDRESS) || 'Karad, Satara, Maharashtra — 415110',
  gstin: ((typeof process !== 'undefined' && process.env && process.env.REACT_APP_DE_GSTIN) || '27ABCDE1234F1Z5').toUpperCase(),
  pan: (typeof process !== 'undefined' && process.env && process.env.REACT_APP_DE_PAN) || 'ABCDE1234F',
  stateCode: (typeof process !== 'undefined' && process.env && process.env.REACT_APP_DE_STATE_CODE) || '27',
  stateName: 'Maharashtra',
};

const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;

function round2(n) {
  return Math.round((Number(n) + Number.EPSILON) * 100) / 100;
}

function stateFromGstin(gstin) {
  if (!gstin || String(gstin).length < 2) return null;
  const code = String(gstin).trim().toUpperCase().slice(0, 2);
  return {
    code,
    name: STATE_CODES[code] || `State ${code}`,
  };
}

/**
 * GSTIN format + checksum (CGST registration identity).
 * Checksum uses Luhn-like mod-36 over characters 0-9A-Z.
 */
function validateGstin(gstin, options = {}) {
  const { requireChecksum = true } = options;
  const value = String(gstin || '').trim().toUpperCase();
  if (!value) {
    return { ok: false, error: 'GSTIN is required' };
  }
  if (value.length !== 15) {
    return { ok: false, error: 'GSTIN must be 15 characters' };
  }
  if (!GSTIN_RE.test(value)) {
    return { ok: false, error: 'Invalid GSTIN format' };
  }

  const state = stateFromGstin(value);
  const base = {
    gstin: value,
    stateCode: state.code,
    stateName: state.name,
    pan: value.slice(2, 12),
  };

  if (!requireChecksum) {
    return { ok: true, ...base, checksumChecked: false };
  }

  const chars = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  let factor = 1;
  let sum = 0;
  for (let i = 0; i < 14; i += 1) {
    const codePoint = chars.indexOf(value[i]);
    if (codePoint < 0) {
      return { ok: false, error: 'Invalid GSTIN character' };
    }
    let product = codePoint * factor;
    factor = factor === 1 ? 2 : 1;
    product = Math.floor(product / chars.length) + (product % chars.length);
    sum += product;
  }
  const check = (chars.length - (sum % chars.length)) % chars.length;
  if (value[14] !== chars[check]) {
    return { ok: false, error: 'Invalid GSTIN checksum', checksumExpected: chars[check] };
  }

  return {
    ok: true,
    ...base,
    checksumChecked: true,
  };
}

function isValidInvoiceSerial(serial) {
  const s = String(serial || '').trim();
  if (!s || s.length > INVOICE_SERIAL_MAX_LEN) return false;
  return /^[A-Za-z0-9][A-Za-z0-9\-\/]*$/.test(s);
}

/**
 * Place of supply for B2B services: recipient location (IGST Act).
 * Fallback: work-site state, then supplier state (intra-State).
 */
function resolvePlaceOfSupply({
  supplierGstin = PLATFORM_SUPPLIER.gstin,
  recipientGstin = null,
  recipientStateCode = null,
  siteStateCode = null,
} = {}) {
  const supplier = stateFromGstin(supplierGstin) || {
    code: PLATFORM_SUPPLIER.stateCode,
    name: PLATFORM_SUPPLIER.stateName,
  };

  let recipient = null;
  if (recipientGstin) {
    const v = validateGstin(recipientGstin);
    if (v.ok) recipient = { code: v.stateCode, name: v.stateName };
  }
  if (!recipient && recipientStateCode) {
    const code = String(recipientStateCode).padStart(2, '0');
    recipient = { code, name: STATE_CODES[code] || `State ${code}` };
  }
  if (!recipient && siteStateCode) {
    const code = String(siteStateCode).padStart(2, '0');
    recipient = { code, name: STATE_CODES[code] || `State ${code}` };
  }
  if (!recipient) recipient = { ...supplier };

  const interState = supplier.code !== recipient.code;
  return {
    supplier,
    recipient,
    placeOfSupply: recipient,
    interState,
    supplyType: interState ? 'INTER_STATE' : 'INTRA_STATE',
  };
}

/**
 * Output tax on taxable value (s.9 levy; s.33 — tax indicated on invoice).
 */
function computeOutputTax(taxableValue, place = null) {
  const taxable = round2(taxableValue);
  const pos = place || resolvePlaceOfSupply();
  if (taxable <= 0) {
    return {
      taxable: 0,
      rate: OUTPUT_GST_RATE,
      gst: 0,
      cgst: 0,
      sgst: 0,
      igst: 0,
      total: 0,
      interState: pos.interState,
      supplyType: pos.supplyType,
      placeOfSupply: pos.placeOfSupply,
      components: [],
    };
  }

  if (pos.interState) {
    const igst = round2(taxable * IGST_RATE);
    return {
      taxable,
      rate: IGST_RATE,
      gst: igst,
      cgst: 0,
      sgst: 0,
      igst,
      total: round2(taxable + igst),
      interState: true,
      supplyType: 'INTER_STATE',
      placeOfSupply: pos.placeOfSupply,
      components: [{ label: 'IGST', rate: IGST_RATE, amount: igst }],
    };
  }

  const cgst = round2(taxable * CGST_RATE);
  const sgst = round2(taxable * SGST_RATE);
  const gst = round2(cgst + sgst);
  return {
    taxable,
    rate: OUTPUT_GST_RATE,
    gst,
    cgst,
    sgst,
    igst: 0,
    total: round2(taxable + gst),
    interState: false,
    supplyType: 'INTRA_STATE',
    placeOfSupply: pos.placeOfSupply,
    components: [
      { label: 'CGST', rate: CGST_RATE, amount: cgst },
      { label: 'SGST', rate: SGST_RATE, amount: sgst },
    ],
  };
}

/**
 * s.52 TCS on net value of taxable supplies (taxable / consideration base — not GST-inclusive).
 */
function computeTcs(taxableValue, rate = TCS_RATE_DEFAULT) {
  const base = round2(taxableValue);
  const pct = Math.min(Math.max(Number(rate) || 0, 0), TCS_RATE_MAX);
  const amount = round2(base * pct);
  return {
    base,
    rate: pct,
    ratePct: round2(pct * 100),
    amount,
    legalMaxRate: TCS_RATE_MAX,
    citation: LEGAL.citations.tcs,
    remittanceNote:
      `Pay to Government within ${TCS_REMIT_DAYS_AFTER_MONTH} days after month-end (s.52(3)); file GSTR-8.`,
  };
}

/**
 * Rule 46 tax-invoice payload for PDF / API.
 * When ownerGstRegistered is false → Bill of Supply (s.31(3)(c) / r.49), zero tax.
 */
function buildTaxInvoiceFields({
  invoiceNo,
  invoiceDate,
  taxableValue,
  description,
  quantity,
  unit = 'hrs',
  ratePerUnit,
  sac = DEFAULT_SAC,
  recipientName,
  recipientAddress,
  recipientGstin = null,
  recipientStateCode = null,
  siteStateCode = null,
  placeOfSupplyName = null,
  reverseCharge = false,
  supplier = PLATFORM_SUPPLIER,
  ownerGstRegistered = true,
} = {}) {
  const serialOk = isValidInvoiceSerial(invoiceNo);
  const pos = resolvePlaceOfSupply({
    supplierGstin: supplier.gstin,
    recipientGstin,
    recipientStateCode,
    siteStateCode,
  });
  const isBillOfSupply = !ownerGstRegistered;
  const tax = isBillOfSupply
    ? {
        taxable: round2(taxableValue),
        rate: 0,
        gst: 0,
        cgst: 0,
        sgst: 0,
        igst: 0,
        total: round2(taxableValue),
        interState: false,
        supplyType: 'EXEMPT',
        placeOfSupply: pos.placeOfSupply,
        components: [],
      }
    : computeOutputTax(taxableValue, pos);
  const recipientRegistered = Boolean(recipientGstin && validateGstin(recipientGstin).ok);
  const needsUnregAddress =
    !recipientRegistered && tax.taxable >= UNREGISTERED_ADDRESS_THRESHOLD;

  return {
    legal: {
      documentType: isBillOfSupply ? 'BILL_OF_SUPPLY' : 'TAX_INVOICE',
      title: isBillOfSupply
        ? 'Bill of Supply (Original for Recipient)'
        : 'GST Tax Invoice (Original for Recipient)',
      citations: isBillOfSupply
        ? [LEGAL.citations.billOfSupply]
        : [
            LEGAL.citations.taxInvoice,
            LEGAL.citations.taxIndicated,
            LEGAL.citations.invoiceParticulars,
          ],
      source: LEGAL.source,
    },
    supplier: {
      name: supplier.name,
      address: supplier.address,
      gstin: supplier.gstin,
      stateCode: pos.supplier.code,
      stateName: pos.supplier.name,
    },
    recipient: {
      name: recipientName || '—',
      address: recipientAddress || '—',
      gstin: recipientGstin || 'URP',
      registered: recipientRegistered,
      stateCode: pos.recipient.code,
      stateName: pos.recipient.name,
      addressRequired: needsUnregAddress,
    },
    invoice: {
      number: invoiceNo,
      numberValid: serialOk,
      maxSerialLength: INVOICE_SERIAL_MAX_LEN,
      date: invoiceDate || new Date().toISOString().slice(0, 10),
    },
    line: {
      description: description || 'Construction machinery hire (operator + fuel)',
      sac,
      hsnOrSac: sac,
      quantity,
      unit,
      ratePerUnit: round2(ratePerUnit || 0),
      taxableValue: tax.taxable,
    },
    tax: {
      ...tax,
      reverseCharge: Boolean(reverseCharge),
      placeOfSupplyLabel: isBillOfSupply
        ? 'N/A'
        : (placeOfSupplyName
          || `${pos.placeOfSupply.name} (${pos.placeOfSupply.code})`),
    },
    totals: {
      taxable: tax.taxable,
      gst: tax.gst,
      cgst: tax.cgst,
      sgst: tax.sgst,
      igst: tax.igst,
      grandTotal: tax.total,
    },
  };
}

// WARNING (legal, Aug 2026): Bill of Supply (s.31(3)(c) / r.49) does NOT apply to
// machinery hire (SAC 997313, taxable at 18%). DO NOT call this for owner-unregistered
// bookings — DE output tax is still due. Reserved for future exempt DE supplies only.
/** s.31(3)(c) / r.49 — Bill of Supply: registered person making an exempt supply. */
function buildBillOfSupplyFields(opts = {}) {
  return buildTaxInvoiceFields({ ...opts, ownerGstRegistered: false });
}

/** Booking quote helper — advance is commercial policy; GST is statutory. */
// WARNING (legal, Aug 2026): ownerGstRegistered param is reserved — DO NOT drive off owner
// registration status for machinery hire (SAC 997313). DE is principal supplier for
// managed_fleet bookings (s.52 TCS=nil, bookingFlowV2Routes.js:517). Output GST = 18%
// always. This param is only valid if DE supplies genuinely exempt services (none currently).
function computeBookingTaxQuote(baseAmount, placeOpts = {}, ownerGstRegistered = true) {
  const base = round2(baseAmount);
  const pos = resolvePlaceOfSupply(placeOpts);
  if (ownerGstRegistered === false) {
    return {
      base,
      gst: 0,
      cgst: 0,
      sgst: 0,
      igst: 0,
      grandTotal: base,
      rate: 0,
      interState: false,
      supplyType: 'EXEMPT',
      documentType: 'BILL_OF_SUPPLY',
      placeOfSupply: pos.placeOfSupply,
      components: [],
    };
  }
  const tax = computeOutputTax(base, pos);
  return {
    base: tax.taxable,
    gst: tax.gst,
    cgst: tax.cgst,
    sgst: tax.sgst,
    igst: tax.igst,
    grandTotal: tax.total,
    rate: tax.rate,
    interState: tax.interState,
    supplyType: tax.supplyType,
    placeOfSupply: tax.placeOfSupply,
    components: tax.components,
  };
}

function splitStoredGst(taxableValue, gstAmount, placeOpts = {}) {
  const taxable = round2(taxableValue);
  const gst = round2(gstAmount);
  if (gst <= 0) {
    return computeOutputTax(taxable, resolvePlaceOfSupply(placeOpts));
  }
  const pos = resolvePlaceOfSupply(placeOpts);
  if (pos.interState) {
    return {
      taxable,
      rate: taxable > 0 ? round2(gst / taxable) : OUTPUT_GST_RATE,
      gst,
      cgst: 0,
      sgst: 0,
      igst: gst,
      total: round2(taxable + gst),
      interState: true,
      supplyType: 'INTER_STATE',
      placeOfSupply: pos.placeOfSupply,
      components: [{ label: 'IGST', rate: taxable > 0 ? gst / taxable : IGST_RATE, amount: gst }],
    };
  }
  const half = round2(gst / 2);
  const other = round2(gst - half);
  return {
    taxable,
    rate: taxable > 0 ? round2(gst / taxable) : OUTPUT_GST_RATE,
    gst,
    cgst: half,
    sgst: other,
    igst: 0,
    total: round2(taxable + gst),
    interState: false,
    supplyType: 'INTRA_STATE',
    placeOfSupply: pos.placeOfSupply,
    components: [
      { label: 'CGST', rate: CGST_RATE, amount: half },
      { label: 'SGST', rate: SGST_RATE, amount: other },
    ],
  };
}

export {
  LEGAL,
  OUTPUT_GST_RATE,
  CGST_RATE,
  SGST_RATE,
  IGST_RATE,
  TCS_RATE_MAX,
  TCS_RATE_DEFAULT,
  DEFAULT_SAC,
  INVOICE_SERIAL_MAX_LEN,
  UNREGISTERED_ADDRESS_THRESHOLD,
  TCS_REMIT_DAYS_AFTER_MONTH,
  STATE_CODES,
  PLATFORM_SUPPLIER,
  GSTIN_RE,
  round2,
  stateFromGstin,
  validateGstin,
  isValidInvoiceSerial,
  resolvePlaceOfSupply,
  computeOutputTax,
  computeTcs,
  buildTaxInvoiceFields,
  buildBillOfSupplyFields,
  computeBookingTaxQuote,
  splitStoredGst,
};
